import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEO } from "@/components/seo/SEO";
import { Helmet } from "react-helmet-async";
// Analytics centralized in App.tsx; removed unused import
import { SkipLink } from "@/components/common/SkipLink";
import { CookieConsent } from "@/components/common/CookieConsent";
import { Toaster } from "@/components/ui/toaster";
import { StickyCTA } from "@/components/common/StickyCTA";
import { WhatsAppButton } from "@/components/common/WhatsAppButton";
import { fetchMediumPosts, type MediumPost } from "@/lib/medium";

const PRIMARY_USERNAME = "insorganization.org";
// const PREVIEW_USERNAME = "mehmetyavuzyagis";
const PREVIEW_USERNAME = "insorganization.org";

const formatDate = (dateStr: string, lang: string) => {
  try {
    const formatter = new Intl.DateTimeFormat(lang || "en", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    return formatter.format(new Date(dateStr));
  } catch {
    return dateStr;
  }
};

const BlogPage = () => {
  const { t, i18n } = useTranslation();
  const [posts, setPosts] = useState<MediumPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPreview, setIsPreview] = useState(false);

  useEffect(() => {
    let isActive = true;
    (async () => {
      try {
        if (!isActive) return;
        setLoading(true);
        setError(null);
        const primary = await fetchMediumPosts(PRIMARY_USERNAME);
        if (primary.length > 0) {
          if (!isActive) return;
          setPosts(primary);
          setIsPreview(false);
          setError(null);
        } else if ((import.meta as any).env?.DEV) {
          const preview = await fetchMediumPosts(PREVIEW_USERNAME);
          if (!isActive) return;
          setPosts(preview);
          setIsPreview(true);
          setError(null);
        } else {
          if (!isActive) return;
          setPosts([]);
        }
      } catch (err) {
        console.error(err);
        if (!isActive) return;
        setError(t("blog.error", "Unable to load Medium posts right now."));
      } finally {
        if (!isActive) return;
        setLoading(false);
      }
    })();

    return () => {
      isActive = false;
    };
  }, [t]);

  const headline = useMemo(() => (
    <div className="text-center md:text-left">
      <h1 className="text-4xl md:text-5xl font-bold text-white">
        {t("blog.title", "Field Notes & Updates")}
      </h1>
      <p className="mt-4 text-lg text-white/80">
        {t("blog.subtitle", "Stories from our Medium publication.")}
      </p>
      <div className="mt-6 flex flex-wrap items-center gap-3 justify-center md:justify-start">
        <a
          href="https://medium.com/@insorganization.org"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/10 px-5 py-2 text-sm font-semibold text-white transition hover:bg-white/20"
        >
          {t("blog.visit", "Visit Medium profile")}
        </a>
        {isPreview ? (
          <span className="rounded-full border border-amber-200 bg-amber-100/20 px-4 py-1 text-xs font-semibold text-amber-200">
            {t("blog.preview", "Preview feed for layout")}
          </span>
        ) : null}
      </div>
    </div>
  ), [t, isPreview]);

  return (
    <div className="bg-neutral-50 min-h-screen text-neutral-900 flex flex-col">
      <SkipLink />
      <SEO title={t("blog.title", "Field Notes & Updates")} description={t("blog.subtitle", "Stories from our Medium publication.")} url="/blog" />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://www.insorganizasyon.org",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: t("blog.title", "Field Notes & Updates"),
                item: "https://www.insorganizasyon.org/blog",
              },
            ],
          })}
        </script>
      </Helmet>
      <Header />
      <main id="main-content" className="flex-grow">
        <section className="relative overflow-hidden bg-gradient-to-br from-brand-navy via-brand-navy/80 to-brand-turquoise/60 py-20">
          <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.25),_transparent_55%)]" />
          <div className="relative container mx-auto px-4">
            {headline}
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4 max-w-6xl">
            {error && posts.length === 0 ? (
              <div className="mb-12 rounded-3xl border border-amber-200 bg-amber-50 p-8 text-center text-amber-900">
                <h2 className="text-2xl font-semibold">
                  {t("blog.errorTitle", "We couldn’t reach Medium right now.")}
                </h2>
                <p className="mt-3 text-sm md:text-base">
                  {t("blog.error", "Unable to load Medium posts right now.")}
                </p>
                <a
                  href="https://medium.com/@insorganization.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-4 inline-flex items-center gap-2 rounded-full border border-amber-400 bg-white/70 px-5 py-2 text-xs font-semibold text-amber-900 transition hover:bg-white"
                >
                  {t("blog.visit", "Visit Medium profile")}
                </a>
              </div>
            ) : null}

            {loading ? (
              <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                {Array.from({ length: 6 }).map((_, index) => (
                  <motion.div
                    key={index}
                    className="animate-pulse rounded-3xl border border-neutral-200 bg-white/70 p-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <div className="mb-4 h-44 rounded-2xl bg-neutral-200" />
                    <div className="mb-2 h-5 w-3/4 rounded bg-neutral-200" />
                    <div className="mb-4 h-4 w-1/2 rounded bg-neutral-200" />
                    <div className="space-y-2">
                      <div className="h-3 w-full rounded bg-neutral-200" />
                      <div className="h-3 w-5/6 rounded bg-neutral-200" />
                      <div className="h-3 w-2/3 rounded bg-neutral-200" />
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : null}

            {!loading && posts.length === 0 && !error ? (
              <div className="rounded-3xl border border-brand-turquoise/30 bg-brand-turquoise/10 p-10 text-center">
                <div className="mx-auto mb-5 h-16 w-16 rounded-full border border-brand-turquoise/40 bg-white/80 flex items-center justify-center text-3xl text-brand-turquoise">🧭</div>
                <h2 className="text-2xl font-semibold text-brand-turquoise">
                  {t("blog.comingSoon", "Our field team is currently on the move.")}
                </h2>
                <p className="mt-3 text-neutral-600">
                  {t("blog.follow", "Once they return from the field we’ll share fresh observations with you right here. Follow us on Medium to catch every story the moment it lands.")}
                </p>
              </div>
            ) : null}

            {!loading && posts.length > 0 ? (
              <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                {posts.map((post, idx) => (
                  <motion.article
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.4, delay: idx * 0.05 }}
                    className="group flex h-full flex-col overflow-hidden rounded-3xl border border-neutral-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-xl"
                  >
                    {post.thumbnail ? (
                      <div className="relative h-48 w-full overflow-hidden bg-neutral-200">
                        <img
                          src={post.thumbnail}
                          alt={post.title}
                          loading="lazy"
                          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                        />
                      </div>
                    ) : null}
                    <div className="flex flex-1 flex-col gap-4 p-6">
                      <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-neutral-500">
                        <span>{formatDate(post.pubDate, i18n.language)}</span>
                        <span>{post.author}</span>
                      </div>
                      <h3 className="text-xl font-semibold text-neutral-900 line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="flex-1 text-sm text-neutral-600 line-clamp-4">
                        {post.description}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {post.categories.slice(0, 3).map((category) => (
                          <span
                            key={category}
                            className="rounded-full border border-brand-turquoise/30 bg-brand-turquoise/10 px-3 py-1 text-xs font-medium text-brand-turquoise"
                          >
                            #{category}
                          </span>
                        ))}
                      </div>
                      <div className="pt-2">
                        <a
                          href={post.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm font-semibold text-brand-turquoise transition hover:text-teal-700"
                        >
                          {t("blog.readMore", "Read story")}
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2}>
                            <path d="M5 12h14" />
                            <path d="M13 5l7 7-7 7" />
                          </svg>
                        </a>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </div>
            ) : null}
          </div>
        </section>
      </main>
      <Footer />
      <CookieConsent />
      <Toaster />
      <StickyCTA />
      <WhatsAppButton />
    </div>
  );
};

export default BlogPage;
